import type { Observable } from 'rxjs';
import {
  map,
  forkJoin,
  EMPTY,
  distinct,
  from,
  mergeMap,
  shareReplay,
} from 'rxjs';

import type {
  HotelConstraintQL,
  OverviewDestinationStaticInfo,
  OverviewActivityStaticInfo,
  OverviewHeartedStaticInfo,
  OverviewStaticInfo,
  OverviewStaticInfoResult,
  HotelIngredientAdapter,
  HeartedHotel,
  ActivityStaticInfo,
} from '@libs/trip-planner-interfaces';
import {
  SelectedHotelTypeQL,
  StaticInfoType,
} from '@libs/trip-planner-interfaces';
import { assertValue, getHotelKey, toDictionary, logged } from '@libs/utils';

import { searchHotels } from '../helpers/search';
import { createStaticInfo } from '../helpers/createStaticInfo';

export class OverviewStaticInfoSearchService {
  public search(
    ingredients: HotelIngredientAdapter[]
  ): Observable<OverviewStaticInfoResult> {
    if (!ingredients.length) {
      return EMPTY;
    }

    const destinationDictionary = this.convertToDictionary(
      this.searchDestinationStaticInfo(ingredients)
    );

    const subIngredientsDictionary = this.convertToDictionary(
      this.searchSubIngredients(ingredients)
    );

    const heartedHotelsDictionary = this.convertToDictionary(
      this.searchHeartedHotels(ingredients)
    );

    return forkJoin({
      destinationStaticInfo: destinationDictionary,
      subIngredientsStaticInfo: subIngredientsDictionary,
      heartedHotelsStaticInfo: heartedHotelsDictionary,
    });
  }

  public searchMinimumActivityStaticInfo(
    constraintQL: HotelConstraintQL
  ): Observable<boolean> {
    return searchHotels(constraintQL, StaticInfoType.NameOnly).pipe(
      map((response) => !!response.response.hotels.length)
    );
  }

  public getActivitiesStaticInfo(
    codes: string[]
  ): Observable<ActivityStaticInfo[]> {
    const activitiesContstraint: HotelConstraintQL = {
      hotelCodes: codes,
      selectedHotelTypes: [
        SelectedHotelTypeQL.Ticket,
        SelectedHotelTypeQL.Golf,
      ],
      boardBasis: [],
    };
    return searchHotels(
      activitiesContstraint,
      StaticInfoType.ActivitySuggestions
    ).pipe(map((staticInfoResponse) => staticInfoResponse.response.hotels));
  }

  public searchSuggestedActivitiesStaticInfo(
    constraintQL: HotelConstraintQL
  ): Observable<ActivityStaticInfo[]> {
    const activitiesContstraint: HotelConstraintQL = {
      ...constraintQL,
      selectedHotelTypes: [
        SelectedHotelTypeQL.Golf,
        SelectedHotelTypeQL.Ticket,
      ],
      maxNumberOfHotels: 3,
      boardBasis: [],
    };

    // NOTE: we need to remove hotelCodes from activities constraint to allow search for activities
    delete activitiesContstraint.hotelCodes;

    return searchHotels(
      activitiesContstraint,
      StaticInfoType.ActivitySuggestions
    ).pipe(map((staticInfoResponse) => staticInfoResponse.response.hotels));
  }

  private searchDestinationStaticInfo(
    ingredients: HotelIngredientAdapter[]
  ): Observable<OverviewDestinationStaticInfo> {
    return from(ingredients).pipe(
      mergeMap((ingredient) => {
        if (isEmptyConstraint(ingredient.constraint)) {
          return EMPTY;
        }

        return searchHotels(
          ingredient.constraint,
          StaticInfoType.Overview
        ).pipe(
          mergeMap(({ response: getHotelsResponse, type }) =>
            getHotelsResponse.hotels.map((hotel) =>
              createStaticInfo(
                hotel,
                getHotelsResponse,
                ingredient.ingredientId,
                type
              )
            )
          )
        );
      }),
      shareReplay()
    );

    function isEmptyConstraint(constraint: HotelConstraintQL): boolean {
      const isEmptySerializedConstraint =
        !constraint.serializedConstraint ||
        constraint.serializedConstraint.length === 0;
      const isEmptyDestinationConstraint =
        !constraint.destination || constraint.destination.length === 0;
      const isEmptyPolygonConstraint =
        !constraint.polygon || constraint.polygon.length === 0;
      const isEmptyHotelCodesConstraint =
        !constraint.hotelCodes || constraint.hotelCodes.length === 0;
      const isEmptyTagIdConstraint = !constraint.tagId;
      const isEmptyPolygonIdConstraint = !constraint.polygonId;
      const isEmptyDestinationCodeConstraint = !constraint.destinationCode;

      return (
        isEmptySerializedConstraint &&
        isEmptyDestinationConstraint &&
        isEmptyPolygonConstraint &&
        isEmptyHotelCodesConstraint &&
        isEmptyTagIdConstraint &&
        isEmptyPolygonIdConstraint &&
        isEmptyDestinationCodeConstraint
      );
    }
  }

  private searchSubIngredients(
    ingredients: HotelIngredientAdapter[]
  ): Observable<OverviewActivityStaticInfo> {
    const subIngredients = ingredients.flatMap(
      (ingredient) => ingredient.subIngredients
    );
    const subIngredientCodes = subIngredients.map(
      (subIngredient) => subIngredient.code
    );

    if (subIngredientCodes.length) {
      const constraint: HotelConstraintQL = {
        hotelCodes: subIngredientCodes,
        maxNumberOfHotels: subIngredientCodes.length,
        selectedHotelTypes: [],
        boardBasis: [],
      };

      const subIngredientsStaticInfo = searchHotels(
        constraint,
        StaticInfoType.OverviewActivity
      ).pipe(
        mergeMap(({ response: getHotelsResponse, type }) =>
          getHotelsResponse.hotels.flatMap((hotel) => {
            const filtredIngredients = subIngredients.filter(
              (subIngredient) => subIngredient.code === hotel.hotelCode
            );

            return filtredIngredients.map((filtredIngredient) =>
              createStaticInfo(
                hotel,
                getHotelsResponse,
                filtredIngredient.ingredientId,
                type
              )
            );
          })
        ),
        shareReplay()
      );

      return subIngredientsStaticInfo;
    }

    return EMPTY;
  }

  private searchHeartedHotels(
    ingredients: HotelIngredientAdapter[]
  ): Observable<OverviewHeartedStaticInfo> {
    function getHeartedHotelsCodes(heartedHotels: HeartedHotel[]): string[] {
      return heartedHotels.map((heartedHotel) => heartedHotel.code);
    }
    const hotelCodes = ingredients.flatMap((ingredient) =>
      getHeartedHotelsCodes(ingredient.heartedHotels)
    );

    if (hotelCodes.length) {
      const constraint: HotelConstraintQL = {
        hotelCodes: hotelCodes,
        maxNumberOfHotels: hotelCodes.length,
        selectedHotelTypes: [],
        boardBasis: [],
      };

      const heartedHotelsStaticInfo = searchHotels(
        constraint,
        StaticInfoType.OverviewHeartedHotel
      ).pipe(
        mergeMap(({ response: getHotelsResponse, type }) =>
          getHotelsResponse.hotels.map((hotel) => {
            const ingredientId = ingredients.find((ingredient) =>
              getHeartedHotelsCodes(ingredient.heartedHotels).includes(
                hotel.hotelCode
              )
            )?.ingredientId;

            assertValue(
              ingredientId,
              'overviewStaticInfoSearchService > searchHeartedHotels > ingredientId'
            );

            return createStaticInfo(
              hotel,
              getHotelsResponse,
              ingredientId,
              type
            );
          })
        ),
        shareReplay()
      );

      return heartedHotelsStaticInfo;
    }

    return EMPTY;
  }

  private convertToDictionary<T extends OverviewStaticInfo>(
    overviewObservable: Observable<T>
  ): Observable<Record<string, T>> {
    return overviewObservable.pipe(
      distinct((staticInfo) =>
        getHotelKey(staticInfo.hotelCode, staticInfo.ingredientId)
      ),
      toDictionary((staticInfo) =>
        getHotelKey(staticInfo.hotelCode, staticInfo.ingredientId)
      ),
      logged('overview static info'),
      shareReplay()
    );
  }
}
